import type { GetServerSideProps } from 'next'
import type { SSRConfig } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { SupportedMarketKey } from '@nxt-insurance/constants'
import Spinner from '@nxt-insurance/ui-kit/components/Spinner'

import { runQuery } from '@/cms/client'
import type { Navbar, RouteWithPage } from '@/cms/pages'
import { redirectQuery, routeQuery, siteSettingsQueryNavbar } from '@/cms/pages'
import { Page } from '@/components/Page'
import { PageWithPreview } from '@/components/Page/PageWithPreview'
import { PageMetaDescription } from '@/components/PageMetaDescription'
import Header from '@/components/sections/Header'
import { APP_ENV, DOMAIN } from '@/config/env'
import { useAlternateLinks } from '@/hooks/useAlternateLinks'
import { useAppendCurrentQueryParametersToAnchorTags } from '@/hooks/useAppendQueryParametersToAnchorTags'
import PreviewProvider from '@/pages/PreviewProvider'
import { getSplitClient } from '@/services/splitio'
import { getLocaleParts } from '@/utils/locale'
import { getNavbarFromSplit, getPageFromSplit } from '@/utils/page'
import { getPathWithParams } from '@/utils/url'

type Props = SSRConfig & {
  preview: boolean
  data: {
    // There is actually no problem with this type but Zod has sometimes problems
    // with very deep types
    // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
    // @ts-ignore-next-line
    route: RouteWithPage
    navbar?: Navbar
  }
}

const DynamicNotFoundSection = dynamic(
  async () => import('@/components/sections/NotFoundSection'),
  {
    loading: () => <Spinner />,
    ssr: false,
  }
)
const Footer = dynamic(async () => import('@/components/sections/Footer').then((mod) => mod.Footer))

const Home = (props: Props) => {
  const { locale, locales } = useRouter()
  const [lang, market] = getLocaleParts(locale)
  const { route, navbar } = props.data

  const slugs = useMemo(() => {
    const slugsForAlternateLinks: Record<string, { current: string } | undefined> = {}
    if (!route) {
      return slugsForAlternateLinks
    }

    return (
      locales?.reduce((slugsValue, l) => {
        slugsValue[getLocaleParts(l)[0]] = { current: '' }
        return slugsValue
      }, slugsForAlternateLinks) ?? {}
    )
  }, [route, locales])

  const alternateLinks = useAlternateLinks(slugs, Object.values(SupportedMarketKey))

  useAppendCurrentQueryParametersToAnchorTags()

  if (!route) {
    return (
      <>
        <PageMetaDescription />
        <Header pageType="not_found" navbar={navbar} alternateLinks={alternateLinks} />
        <DynamicNotFoundSection />
        <Footer />
      </>
    )
  }

  if (!props.preview) {
    return <Page route={route} navbar={navbar} alternateLinks={alternateLinks} />
  }

  return (
    <PreviewProvider>
      <PageWithPreview
        navbar={navbar}
        alternateLinks={alternateLinks}
        slug={'index'}
        lang={lang}
        market={market}
      />
    </PreviewProvider>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  // This value is considered fresh for 5 min (s-maxage=300).
  // If a request is repeated within the next 5 min, the previously
  // cached value will still be fresh. If the request is repeated before 359 seconds,
  // the cached value will be stale but still render (stale-while-revalidate=359).
  //
  // In the background, a revalidation request will be made to populate the cache
  // with a fresh value. If you refresh the page, you will see the new value.
  if (APP_ENV !== 'development') {
    ctx.res.setHeader('Cache-Control', 'public, s-maxage=300, stale-while-revalidate=359')
  }
  const locale =
    ctx.locale === 'default' || ctx.locale === undefined
      ? (ctx.defaultLocale as string)
      : ctx.locale
  if (locale === 'default') {
    return {
      notFound: true,
    }
  }
  if (!locale) {
    throw new Error('Assertion: missing locale')
  }
  const requestUrl = new URL(ctx.resolvedUrl, `https://${DOMAIN}`)
  const query = requestUrl.searchParams

  const splitClient = getSplitClient()
  await splitClient.ready()

  const [lang, market] = getLocaleParts(locale)

  const path = `/${lang}-${market}`
  const [route, siteSettingsNavbar, redirect] = await Promise.all([
    runQuery(routeQuery, {
      lang,
      market,
      slug: 'index',
      folder: null,
    }),
    runQuery(siteSettingsQueryNavbar, { lang, market }),
    runQuery(redirectQuery, { path }),
  ])

  if (redirect) {
    return {
      redirect: {
        destination: getPathWithParams(redirect.newPath, { params: query.toString() }),
        permanent: false,
      },
    }
  }

  if (!route) {
    return {
      notFound: true,
    }
  }

  if (route.redirectActive) {
    return {
      redirect: {
        destination: getPathWithParams(route.redirectTarget ?? '/', { params: query.toString() }),
        permanent: false,
      },
    }
  }

  if (APP_ENV === 'production' && !route.availableEnvironments?.includes('production')) {
    return {
      notFound: true,
    }
  }

  if (!route.page || !siteSettingsNavbar) {
    return {
      notFound: true,
    }
  }

  const [page, navbar] = await Promise.all([
    getPageFromSplit(route, lang, ctx.req, ctx.res),
    getNavbarFromSplit(siteSettingsNavbar.navbar, lang, { req: ctx.req, res: ctx.res }),
  ])

  if (!page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      preview: ctx.preview ?? false,
      data: {
        navbar,
        route: {
          ...route,
          page,
        },
      },
    },
  }
}

export default Home
